import type { IAppState } from "@fortress/shared";
import { httpBase } from "./net/origin";

// The client's view of the server's runtime config (`IAppState`). Fetched once
// from `GET /app-state` and memoised — this is the FIRST thing the app reads on
// boot, before any auth gating, so the server stays the single source of truth
// for toggles like AUTH_DISABLED instead of the client carrying its own copy.

let cached: Promise<IAppState> | null = null;

/** Fetch (once) and return the server's app state. */
export function getAppState(): Promise<IAppState> {
  if (!cached) {
    cached = fetch(`${httpBase()}/app-state`).then((res) => {
      if (!res.ok) throw new Error(`app-state HTTP ${res.status}`);
      return res.json() as Promise<IAppState>;
    });
  }
  return cached;
}

/** The server's CURRENT `appVersion`, fetched fresh (never the boot memo) so a
 * long-open tab can spot a deploy. `null` when unreachable or the server
 * predates the field — never throws; the version poll just tries again. */
export async function fetchServerAppVersion(): Promise<number | null> {
  try {
    const res = await fetch(`${httpBase()}/app-state`, { cache: "no-store" });
    if (!res.ok) return null;
    const state = (await res.json()) as IAppState;
    return typeof state.appVersion === "number" ? state.appVersion : null;
  } catch {
    return null;
  }
}
